import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  Tooltip,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

interface Project {
  id: string;
  name: string;
  notes?: string;
}

interface Department {
  id: string;
  name: string;
  notes?: string;
}

interface WorkType {
  id: string;
  name: string;
  rate?: number;
}

interface DepartmentWithMaterials extends Department {
  materials: Array<{ id: string; name: string; unit: string }>;
}

const ReportForm: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [projects, setProjects] = useState<Project[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [workTypes, setWorkTypes] = useState<WorkType[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<DepartmentWithMaterials | null>(null);
  const [hasMaterials, setHasMaterials] = useState(false);
  const [selectedMaterials, setSelectedMaterials] = useState<Record<string, { quantity: string; comment: string }>>({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    dateFrom: new Date(),
    dateTo: new Date(),
    projectId: '',
    departmentId: '',
    workTypeId: '',
    hours: 0,
    minutes: 0,
    notes: '',
    currentDay: true,
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (formData.departmentId) {
      loadDepartmentMaterials(formData.departmentId);
    }
  }, [formData.departmentId]);

  // Separate effect to pre-fill defaults after user data is loaded
  useEffect(() => {
    if (user && (user.defaultDepartmentId || user.defaultWorkTypeId)) {
      setFormData((prev) => ({
        ...prev,
        departmentId: user.defaultDepartmentId || prev.departmentId,
        workTypeId: user.defaultWorkTypeId || prev.workTypeId,
      }));

      if (user.defaultDepartmentId) {
        loadDepartmentMaterials(user.defaultDepartmentId);
      }
    }
  }, [user]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [projectsRes, departmentsRes, workTypesRes] = await Promise.all([
        api.get('/reference/projects'),
        api.get('/reference/departments'),
        api.get('/reference/work-types'),
      ]);
      setProjects(projectsRes.data || []);
      setDepartments(departmentsRes.data || []);
      setWorkTypes(workTypesRes.data || []);
    } catch (err: any) {
      console.error('Failed to load data:', err);
      setError(err.response?.data?.error || 'Failed to load form data. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const loadDepartmentMaterials = async (departmentId: string) => {
    try {
      const res = await api.get(`/reference/departments/${departmentId}`);
      setSelectedDepartment(res.data);
      // Reset selected materials when department changes
      setSelectedMaterials({});
    } catch (err) {
      console.error('Failed to load department materials:', err);
    }
  };

  const handleCurrentDayChange = (checked: boolean) => {
    const today = new Date();
    setFormData({
      ...formData,
      currentDay: checked,
      dateFrom: today,
      dateTo: today,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validation
    if (new Date(formData.dateFrom) > new Date(formData.dateTo)) {
      setError('Date "from" cannot be after date "to"');
      setLoading(false);
      return;
    }

    if (!formData.projectId || !formData.departmentId || !formData.workTypeId) {
      setError('Please fill all required fields');
      setLoading(false);
      return;
    }

    // Validate hours and minutes
    if (formData.hours < 0 || formData.hours > 23) {
      setError(t('report.validation.hoursRange'));
      setLoading(false);
      return;
    }
    if (formData.minutes < 0 || formData.minutes > 59) {
      setError(t('report.validation.minutesRange'));
      setLoading(false);
      return;
    }

    try {
      // Validate that projectId is actually set
      if (!formData.projectId) {
        setError(t('report.validation.selectProject'));
        setLoading(false);
        return;
      }

      // Format materials text from selected materials
      let materialsText = null;
      if (hasMaterials && selectedDepartment?.materials) {
        const materialsLines = selectedDepartment.materials
          .filter((m) => selectedMaterials[m.id] && selectedMaterials[m.id].quantity)
          .map((m) => {
            const material = selectedMaterials[m.id];
            const baseLine = `${m.name}     ${m.unit}; ${material.quantity}`;
            return material.comment ? `${baseLine} ${material.comment}` : baseLine;
          });
        materialsText = materialsLines.length > 0 ? materialsLines.join('\n') : null;
      }

      const submitData = {
        projectId: formData.projectId,
        departmentId: formData.departmentId,
        workTypeId: formData.workTypeId,
        dateFrom: formData.dateFrom.toISOString().split('T')[0],
        dateTo: formData.dateTo.toISOString().split('T')[0],
        timeSpent: `${formData.hours.toString().padStart(2, '0')}:${formData.minutes.toString().padStart(2, '0')}`,
        materials: materialsText,
        notes: formData.notes || null,
      };

      console.log('Submitting report with data:', submitData);
      
      await api.post('/reports', submitData);
      navigate('/reports');
    } catch (err: any) {
      console.error('Report submission error:', err);
      setError(err.response?.data?.error || 'Failed to submit report');
    } finally {
      setLoading(false);
    }
  };

  const handleMaterialToggle = (materialId: string, checked: boolean) => {
    if (checked) {
      setSelectedMaterials((prev) => ({
        ...prev,
        [materialId]: { quantity: '', comment: '' },
      }));
    } else {
      setSelectedMaterials((prev) => {
        const newState = { ...prev };
        delete newState[materialId];
        return newState;
      });
    }
  };

  const handleMaterialQuantityChange = (materialId: string, quantity: string) => {
    setSelectedMaterials((prev) => ({
      ...prev,
      [materialId]: { ...prev[materialId], quantity },
    }));
  };

  const handleMaterialCommentChange = (materialId: string, comment: string) => {
    setSelectedMaterials((prev) => ({
      ...prev,
      [materialId]: { ...prev[materialId], comment },
    }));
  };

  return (
    <Container maxWidth="md">
      <Typography variant="h4" gutterBottom>
        {t('report.title')}
      </Typography>
      <Paper sx={{ p: 3, mt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box component="form" onSubmit={handleSubmit}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.currentDay}
                  onChange={(e) => handleCurrentDayChange(e.target.checked)}
                />
              }
              label="Current day"
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <DatePicker
                label="Date From"
                value={formData.dateFrom}
                onChange={(date: Date | null) =>
                  setFormData({ ...formData, dateFrom: date || new Date() })
                }
                disabled={formData.currentDay}
                renderInput={(params: any) => <TextField {...params} fullWidth />}
              />
              <DatePicker
                label="Date To"
                value={formData.dateTo}
                onChange={(date: Date | null) =>
                  setFormData({ ...formData, dateTo: date || new Date() })
                }
                disabled={formData.currentDay}
                renderInput={(params: any) => <TextField {...params} fullWidth />}
              />
            </Box>
          </LocalizationProvider>

          <FormControl fullWidth required sx={{ mb: 2 }} error={!formData.projectId && projects.length > 0}>
            <InputLabel id="project-select-label">Project</InputLabel>
            <Select
              labelId="project-select-label"
              id="project-select"
              value={formData.projectId || ''}
              label="Project"
              onChange={(e) => {
                const selectedProjectId = e.target.value;
                console.log('Project selected:', selectedProjectId, 'Available projects:', projects);
                setFormData({ ...formData, projectId: selectedProjectId });
              }}
              disabled={loading || projects.length === 0}
            >
              {projects.length === 0 ? (
                <MenuItem disabled>No projects available</MenuItem>
              ) : (
                projects.map((project) => (
                  <MenuItem key={project.id} value={project.id}>
                    {project.name}
                  </MenuItem>
                ))
              )}
            </Select>
            {formData.projectId && projects.find((p) => p.id === formData.projectId)?.notes && (
              <Typography variant="caption" sx={{ mt: 0.5, ml: 1.5 }}>
                {projects.find((p) => p.id === formData.projectId)?.notes}
              </Typography>
            )}
            {!formData.projectId && projects.length > 0 && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                Please select a project
              </Typography>
            )}
          </FormControl>

          <FormControl fullWidth required sx={{ mb: 2 }} error={!formData.departmentId && departments.length > 0}>
            <InputLabel id="department-select-label">Department</InputLabel>
            <Select
              labelId="department-select-label"
              id="department-select"
              value={formData.departmentId || ''}
              label="Department"
              onChange={(e) => {
                const selectedDepartmentId = e.target.value;
                setFormData({ ...formData, departmentId: selectedDepartmentId });
              }}
              disabled={loading || departments.length === 0}
            >
              {departments.length === 0 ? (
                <MenuItem disabled>No departments available</MenuItem>
              ) : (
                departments.map((dept) => (
                  <MenuItem key={dept.id} value={dept.id}>
                    {dept.name}
                  </MenuItem>
                ))
              )}
            </Select>
            {formData.departmentId && departments.find((d) => d.id === formData.departmentId)?.notes && (
              <Typography variant="caption" sx={{ mt: 0.5, ml: 1.5 }}>
                {departments.find((d) => d.id === formData.departmentId)?.notes}
              </Typography>
            )}
            {!formData.departmentId && departments.length > 0 && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                Please select a department
              </Typography>
            )}
          </FormControl>

          <FormControl fullWidth required sx={{ mb: 2 }} error={!formData.workTypeId && workTypes.length > 0}>
            <InputLabel id="worktype-select-label">Work Type</InputLabel>
            <Select
              labelId="worktype-select-label"
              id="worktype-select"
              value={formData.workTypeId || ''}
              label="Work Type"
              onChange={(e) => {
                const selectedWorkTypeId = e.target.value;
                setFormData({ ...formData, workTypeId: selectedWorkTypeId });
              }}
              disabled={loading || workTypes.length === 0}
            >
              {workTypes.length === 0 ? (
                <MenuItem disabled>No work types available</MenuItem>
              ) : (
                workTypes.map((wt) => (
                  <MenuItem key={wt.id} value={wt.id}>
                    {wt.name}
                  </MenuItem>
                ))
              )}
            </Select>
            {!formData.workTypeId && workTypes.length > 0 && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                Please select a work type
              </Typography>
            )}
          </FormControl>

          <FormControlLabel
            control={
              <Checkbox
                checked={hasMaterials}
                onChange={(e) => {
                  setHasMaterials(e.target.checked);
                  if (!e.target.checked) {
                    setSelectedMaterials({});
                  }
                }}
              />
            }
            label="Has Materials"
            sx={{ mb: 1 }}
          />

          {hasMaterials && (
            <Box sx={{ mb: 2 }}>
              {!formData.departmentId ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('report.selectDepartmentFirst')}
                </Alert>
              ) : selectedDepartment?.materials && selectedDepartment.materials.length > 0 ? (
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" sx={{ width: 50 }}>{t('report.select')}</TableCell>
                        <TableCell>{t('report.materials')}</TableCell>
                        <TableCell sx={{ width: 150 }}>{t('report.quantity')}</TableCell>
                        <TableCell>{t('report.commentOptional')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedDepartment.materials.map((material) => {
                        const isSelected = !!selectedMaterials[material.id];
                        return (
                          <TableRow key={material.id}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={isSelected}
                                onChange={(e) => handleMaterialToggle(material.id, e.target.checked)}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {material.name} ({material.unit})
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {isSelected ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={selectedMaterials[material.id]?.quantity || ''}
                                  onChange={(e) => handleMaterialQuantityChange(material.id, e.target.value)}
                                  placeholder="0"
                                  inputProps={{ min: 0, step: 0.01 }}
                                  fullWidth
                                />
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  -
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {isSelected ? (
                                <TextField
                                  size="small"
                                  value={selectedMaterials[material.id]?.comment || ''}
                                  onChange={(e) => handleMaterialCommentChange(material.id, e.target.value)}
                                  placeholder={t('report.commentOptional')}
                                  fullWidth
                                />
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  -
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('report.noMaterialsAvailable')}
                </Alert>
              )}
            </Box>
          )}

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('report.timeSpent')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label={t('report.hours')}
                type="number"
                value={formData.hours || ''}
                onChange={(e) => {
                  const value = e.target.value === '' ? 0 : parseInt(e.target.value);
                  if (!isNaN(value)) {
                    const hours = Math.max(0, Math.min(23, value));
                    setFormData({ ...formData, hours });
                  } else if (e.target.value === '') {
                    setFormData({ ...formData, hours: 0 });
                  }
                }}
                required
                inputProps={{ min: 0, max: 23, step: 1 }}
                sx={{ width: 150 }}
                error={formData.hours < 0 || formData.hours > 23}
                helperText={
                  formData.hours < 0 || formData.hours > 23
                    ? t('report.validation.hoursRange')
                    : t('report.hoursHint', { defaultValue: '0-23' })
                }
                placeholder="0"
              />
              <TextField
                label={t('report.minutes')}
                type="number"
                value={formData.minutes || ''}
                onChange={(e) => {
                  const value = e.target.value === '' ? 0 : parseInt(e.target.value);
                  if (!isNaN(value)) {
                    const minutes = Math.max(0, Math.min(59, value));
                    setFormData({ ...formData, minutes });
                  } else if (e.target.value === '') {
                    setFormData({ ...formData, minutes: 0 });
                  }
                }}
                required
                inputProps={{ min: 0, max: 59, step: 1 }}
                sx={{ width: 150 }}
                error={formData.minutes < 0 || formData.minutes > 59}
                helperText={
                  formData.minutes < 0 || formData.minutes > 59
                    ? t('report.validation.minutesRange')
                    : t('report.minutesHint', { defaultValue: '0-59' })
                }
                placeholder="0"
              />
            </Box>
          </Box>

          <TextField
            fullWidth
            multiline
            rows={4}
            label={t('report.notes')}
            value={formData.notes}
            onChange={(e) =>
              setFormData({ ...formData, notes: e.target.value })
            }
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              fullWidth
            >
              {t('report.submitReport')}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/')}
              fullWidth
            >
              {t('common.cancel')}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default ReportForm;

